import {
    getLocalStorageItem,
    LOCAL_STORAGE_KEYS,
    removeLocalStorageItem,
    setLocalStorageItem,
} from '@/setup/local-storage'

/**
 * Board-signup attribution carriage.
 *
 * When a signed-out visitor opens the signup modal from a public ad board, we stash the board
 * owner's referral code synchronously (before any navigation). The signup step then reads it into
 * Clerk `unsafe_metadata`, and the `user.created` webhook stamps it on the new org — so attribution
 * rides INSIDE the signup rather than on the best-effort tracking POST, which races (and loses to)
 * the signup navigation, especially Google OAuth. See the API `SIGN_UP_CONSTS.BoardReferralCodeMetadataKey`.
 *
 * Read then clear are split on purpose: the signup step PEEKs at metadata-build time and only CLEARs
 * after a confirmed-successful signup. Clearing eagerly would drop the referral if the signup call
 * fails and the visitor corrects the error and resubmits (or switches provider).
 */

/**
 * How long a stashed code stays valid. It only has to survive from the board click to the signup
 * call in the same tab (seconds — the Google OAuth round-trip or an email-code entry at most), so a
 * generous hour still comfortably covers every real flow. Past it the stash is treated as stale and
 * ignored, so a visitor who clicks a board CTA, abandons the modal, and signs up hours later from an
 * unrelated surface is not misattributed to a board they merely browsed.
 */
export const BOARD_REFERRAL_STASH_TTL_MS = 60 * 60 * 1000

interface IStashedBoardReferral {
    code: string
    storedAt: number
}

/** Stash the board owner's referral code for the signup that follows. No-op when there is none. */
export function stashBoardReferralCode(referralCode: string | null, now: number = Date.now()): void {
    if (!referralCode) return
    const entry: IStashedBoardReferral = { code: referralCode, storedAt: now }
    setLocalStorageItem(LOCAL_STORAGE_KEYS.SignUp.BoardReferralCode, entry)
}

/**
 * Read the stashed code without clearing it, returning it only when still fresh. Called at
 * metadata-build time so a failed-then-retried signup keeps the referral. A stale (past-TTL) or
 * malformed entry — e.g. a raw-string stash from a previous deploy — is cleared and ignored, so it
 * cannot linger or misattribute a later unrelated signup.
 */
export function peekBoardReferralCode(now: number = Date.now()): string | undefined {
    const entry = getLocalStorageItem<IStashedBoardReferral>(LOCAL_STORAGE_KEYS.SignUp.BoardReferralCode)
    if (entry === undefined) return undefined

    const isValid = typeof entry.code === 'string' && typeof entry.storedAt === 'number'
    if (!isValid || now - entry.storedAt > BOARD_REFERRAL_STASH_TTL_MS) {
        removeLocalStorageItem(LOCAL_STORAGE_KEYS.SignUp.BoardReferralCode)
        return undefined
    }

    return entry.code
}

/** Clear the stash. Called once a signup has actually succeeded so it can never be reused. */
export function clearBoardReferralCode(): void {
    removeLocalStorageItem(LOCAL_STORAGE_KEYS.SignUp.BoardReferralCode)
}
